import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { BrandListSortCol, TBrandList, TBrandListResponse, TBrandListQuery, TBrandListSortCol, adaptBrandFromListResponse, adaptBrandListQueryToRequest } from '../../types';
import { BACKEND_ADMIN_BRAND } from '../../backend-paths';


export const brandListGetApi = createAsyncThunk(
	BACKEND_ADMIN_BRAND,
	(payload: TBrandListQuery | undefined, thunkApi) => {
		const params = adaptBrandListQueryToRequest(payload);

		return HttpService.get<TBrandListResponse>(BACKEND_ADMIN_BRAND, { params }).then(result => {
			return adaptBrandFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TBrandList;
	query: TBrandListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: BrandListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const brandListSlice = createSlice({
	name: 'brandListSlice',
	initialState,
	reducers: {
		setBrandListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setBrandListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setBrandListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setBrandListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setBrandListQuerySortCol(state, action: PayloadAction<TBrandListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearBrandList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[brandListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[brandListGetApi.fulfilled.type]: (state, action: PayloadAction<TBrandList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[brandListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useBrandListSelector = () => useAppSelector(state => state[brandListSlice.name]);

export const { clearBrandList, setBrandListQueryLimit, setBrandListQueryOffset, setBrandListQuerySort, setBrandListQuerySortCol, setBrandListQuerySearch } = brandListSlice.actions;
